import { gql } from "@apollo/client";

export const GET_SALES_DEALS = gql`
    query GetSalesDeals($pageIndex: Int!, $pageSize: Int!, $orderAsc: Boolean!, $orderField: String!, $filters: SalesDealFilterInput) {
        salesDeals(pagedInput: {
            pageInfo: {
                pageIndex: $pageIndex,
                pageSize: $pageSize
            },
            orderAsc: $orderAsc,
            orderField: $orderField
        }, filters: $filters) {
            items {
                id
                dealNumber
                streetAddress
                suburb
                city
                sellingPrice
                status
                agents {
                    agentId {
                        id
                        firstName
                        lastName
                        email
                    }
                    commissionPercentage
                    role
                }
                commissionInputMethod
                totalCommissionPercentage
                totalCommissionAmount
                totalCommissionExclusiveVAT
                totalCommissionInclusiveVAT
                propertyType
                bedrooms
                bathrooms
                garages
                bondRequired
                bondAmount
                depositReceived
                bondGranted
                ficaChecked
                dateCreated
                dateConcluded
                dateCompleted
                dateFailed
                needsAttention
                createdBy {
                    id
                    firstName
                    lastName
                }
                updatedAt
            }
            pageInfo {
                totalCount
                totalPages
                pageSize
                pageIndex
                hasNextPage
                hasPreviousPage
            }
        }
    }
`;

export const GET_SALES_DEAL = gql`
    query GetSalesDeal($id: ID!) {
        salesDeal(id: $id) {
            id
            dealNumber
            streetAddress
            erfNumber
            suburb
            city
            postalCode
            sellingPrice
            status
            agents {
                agentId {
                    id
                    firstName
                    lastName
                    email
                    primaryPhone
                }
                commissionPercentage
                role
                referralSource
                referralDeduction
            }
            sellers {
                name
                isEntity
                email
                phone
                idNumber
                representativeName
                representativeEmail
                representativePhone
                representativeIdNumber
            }
            purchasers {
                name
                isEntity
                email
                phone
                idNumber
                representativeName
                representativeEmail
                representativePhone
                representativeIdNumber
            }
            commissionInputMethod
            totalCommissionPercentage
            totalCommissionAmount
            totalCommissionExclusiveVAT
            totalCommissionInclusiveVAT
            propertyType
            bedrooms
            bathrooms
            garages
            propertyDescription
            paymentMethod
            cashPayableDate
            bondAmount
            bondGrantDate
            bondGranted
            bondGrantedDate
            sentToSmartTrack
            sentToSmartTrackDate
            depositAmount
            depositReceived
            depositReceivedDate
            depositDueDate
            otpDate
            registrationDate
            daysFromOTPToRegistration
            daysSinceOTP
            dealAgeInDays
            transferAttorney {
                professionalServiceId {
                    id
                    companyName
                    primaryContact {
                        firstName
                        lastName
                        title
                        email
                        cellphone
                        telephone
                    }
                }
                assignedDate
                notes
            }
            bondOriginator {
                professionalServiceId {
                    id
                    companyName
                    primaryContact {
                        firstName
                        lastName
                        title
                        email
                        cellphone
                        telephone
                    }
                }
                assignedDate
                notes
            }
            bondAttorney {
                professionalServiceId {
                    id
                    companyName
                    primaryContact {
                        firstName
                        lastName
                        title
                        email
                        cellphone
                        telephone
                    }
                }
                assignedDate
                notes
            }
            complianceContractors {
                id
                companyName
                type
                specializations
                primaryContact {
                    firstName
                    lastName
                    title
                    email
                    cellphone
                    telephone
                }
            }
            complianceCertificates {
                electrical {
                    companyId {
                        id
                        companyName
                        tradingName
                    }
                    dateRequested
                    dateReceived
                    sentToAttorney
                }
                gas {
                    companyId {
                        id
                        companyName
                        tradingName
                    }
                    dateRequested
                    dateReceived
                    sentToAttorney
                }
                beetle {
                    companyId {
                        id
                        companyName
                        tradingName
                    }
                    dateRequested
                    dateReceived
                    sentToAttorney
                }
            }
            ficaChecked
            ficaCheckDate
            hasReferral
            referralInfo {
                referralNumber
                company
                agentName
                agentEmail
                agentPhone
                commissionPercentage
                commissionOwed
                invoiceReceived
                invoiceNumber
                paid
                datePaid
            }
            suspensiveConditions {
                id
                description
                dueDate
                completed
                completedDate
                overdue
            }
            dateCreated
            dateConcluded
            dateCompleted
            dateFailed
            dateConditionsMet
            failureReason
            notes
            outstandingConditions {
                kind
                label
                dueDate
                overdue
            }
            needsAttention
            transferProgress {
                hoaApplicable
                sarsClearance { date notes }
                municipalityClearance { date notes }
                hoaClearance { date notes }
                sellerSignedTransferAttorney { date notes }
                purchaserSignedBondAttorney { date notes }
                purchaserSignedTransferAttorney { date notes }
                lodgement { date notes }
                registrationPreparation { date notes }
                registered { date notes }
            }
            revertRequests {
                id
                requestedBy {
                    id
                    firstName
                    lastName
                }
                requestedAt
                reason
                status
                decidedBy {
                    id
                    firstName
                    lastName
                }
                decidedAt
                decisionNote
            }
            createdBy {
                id
                firstName
                lastName
            }
            updatedBy {
                id
                firstName
                lastName
            }
            updatedAt
        }
    }
`;

export const GET_PIPELINE_METRICS = gql`
    query GetPipelineMetrics($startDate: Date, $endDate: Date, $agentId: ID, $status: SalesDealStatus) {
        pipelineMetrics(startDate: $startDate, endDate: $endDate, agentId: $agentId, status: $status) {
            statusCounts {
                status
                count
                totalValue
                totalCommission
            }
            overallStats {
                totalDeals
                totalValue
                totalCommission
                averageDealValue
                averageCommission
            }
            agentPerformance {
                agentId {
                    id
                    firstName
                    lastName
                }
                deals
                totalCommission
                averageDealValue
            }
        }
    }
`;

export const GET_DEALS_REQUIRING_ATTENTION = gql`
    query GetDealsRequiringAttention {
        dealsRequiringAttention {
            id
            dealNumber
            streetAddress
            status
            agents {
                agentId {
                    id
                    firstName
                    lastName
                }
            }
            ficaChecked
            suspensiveConditions {
                id
                description
                dueDate
                completed
            }
            depositReceived
            depositDueDate
            paymentMethod
            bondGranted
            bondGrantDate
            sentToSmartTrack
            dateCreated
        }
    }
`;

export const CREATE_SALES_DEAL = gql`
    mutation CreateSalesDeal($inputData: SalesDealInput!) {
        createSalesDeal(inputData: $inputData) {
            id
            dealNumber
            streetAddress
            sellingPrice
            status
            agents {
                agentId {
                    id
                    firstName
                    lastName
                }
                commissionPercentage
                role
            }
            commissionInputMethod
            totalCommissionPercentage
            totalCommissionAmount
            totalCommissionExclusiveVAT
            totalCommissionInclusiveVAT
            propertyType
            bedrooms
            bathrooms
            garages
            paymentMethod
            cashPayableDate
            bondAmount
            bondGrantDate
            ficaChecked
            dateCreated
        }
    }
`;

export const UPDATE_SALES_DEAL = gql`
    mutation UpdateSalesDeal($id: ID!, $inputData: SalesDealInput!) {
        updateSalesDeal(id: $id, inputData: $inputData) {
            id
            dealNumber
            streetAddress
            sellingPrice
            status
            agents {
                agentId {
                    id
                    firstName
                    lastName
                }
                commissionPercentage
                role
            }
            commissionInputMethod
            totalCommissionPercentage
            totalCommissionAmount
            totalCommissionExclusiveVAT
            totalCommissionInclusiveVAT
            propertyType
            bedrooms
            bathrooms
            garages
            paymentMethod
            cashPayableDate
            bondAmount
            bondGrantDate
            ficaChecked
            updatedAt
        }
    }
`;

export const UPDATE_SALES_DEAL_STATUS = gql`
    mutation UpdateSalesDealStatus($id: ID!, $status: SalesDealStatus!, $statusData: String) {
        updateSalesDealStatus(id: $id, status: $status, statusData: $statusData) {
            id
            status
            dateConcluded
            dateCompleted
            dateFailed
            failureReason
            updatedAt
        }
    }
`;

export const CONCLUDE_DEAL = gql`
    mutation ConcludeDeal($id: ID!, $inputData: ConcludeDealInput!) {
        concludeDeal(id: $id, inputData: $inputData) {
            id
            status
            dateConcluded
            notes
            updatedAt
        }
    }
`;

export const COMPLETE_DEAL = gql`
    mutation CompleteDeal($id: ID!, $inputData: CompleteDealInput!) {
        completeDeal(id: $id, inputData: $inputData) {
            id
            status
            dateCompleted
            notes
            updatedAt
        }
    }
`;

export const FAIL_DEAL = gql`
    mutation FailDeal($id: ID!, $reason: String!) {
        failDeal(id: $id, reason: $reason) {
            id
            status
            dateFailed
            failureReason
            updatedAt
        }
    }
`;

export const ADD_AGENT_TO_DEAL = gql`
    mutation AddAgentToDeal($dealId: ID!, $agentData: SalesDealAgentInput!) {
        addAgentToDeal(dealId: $dealId, agentData: $agentData) {
            id
            agents {
                agentId {
                    id
                    firstName
                    lastName
                }
                commissionPercentage
                role
            }
            totalCommissionPercentage
            totalCommissionExclusiveVAT
            totalCommissionInclusiveVAT
        }
    }
`;

export const REMOVE_AGENT_FROM_DEAL = gql`
    mutation RemoveAgentFromDeal($dealId: ID!, $agentId: ID!) {
        removeAgentFromDeal(dealId: $dealId, agentId: $agentId) {
            id
            agents {
                agentId {
                    id
                    firstName
                    lastName
                }
                commissionPercentage
                role
            }
            totalCommissionPercentage
            totalCommissionExclusiveVAT
            totalCommissionInclusiveVAT
        }
    }
`;

export const VALIDATE_FICA = gql`
    mutation ValidateFICA($dealId: ID!) {
        validateFICA(dealId: $dealId) {
            id
            ficaChecked
            ficaCheckDate
            updatedAt
        }
    }
`;

export const DELETE_SALES_DEAL = gql`
    mutation DeleteSalesDeal($id: ID!) {
        deleteSalesDeal(id: $id)
    }
`;

export const SEARCH_SALES_DEALS = gql`
    query SearchSalesDeals($searchTerm: String!, $pageIndex: Int!, $pageSize: Int!) {
        searchSalesDeals(searchTerm: $searchTerm, pagedInput: {
            pageInfo: {
                pageIndex: $pageIndex,
                pageSize: $pageSize
            }
        }) {
            items {
                id
                dealNumber
                streetAddress
                suburb
                city
                sellingPrice
                status
                agents {
                    agentId {
                        id
                        firstName
                        lastName
                    }
                }
                propertyType
                dateCreated
                dateConcluded
                dateCompleted
            }
            pageInfo {
                totalCount
                totalPages
                pageSize
                pageIndex
                hasNextPage
                hasPreviousPage
            }
        }
    }
`;

export const ASSIGN_TRANSFER_ATTORNEY = gql`
    mutation AssignTransferAttorney($dealId: ID!, $inputData: AssignProfessionalServiceInput!) {
        assignTransferAttorney(dealId: $dealId, inputData: $inputData) {
            id
            transferAttorney {
                professionalServiceId {
                    id
                    companyName
                    primaryContact {
                        firstName
                        lastName
                        email
                        cellphone
                    }
                }
                assignedDate
                notes
            }
            updatedAt
        }
    }
`;

export const ASSIGN_BOND_ORIGINATOR = gql`
    mutation AssignBondOriginator($dealId: ID!, $inputData: AssignProfessionalServiceInput!) {
        assignBondOriginator(dealId: $dealId, inputData: $inputData) {
            id
            bondOriginator {
                professionalServiceId {
                    id
                    companyName
                    primaryContact {
                        firstName
                        lastName
                        email
                        cellphone
                    }
                }
                assignedDate
                notes
            }
            updatedAt
        }
    }
`;

export const ASSIGN_BOND_ATTORNEY = gql`
    mutation AssignBondAttorney($dealId: ID!, $inputData: AssignProfessionalServiceInput!) {
        assignBondAttorney(dealId: $dealId, inputData: $inputData) {
            id
            bondAttorney {
                professionalServiceId {
                    id
                    companyName
                    primaryContact {
                        firstName
                        lastName
                        email
                        cellphone
                    }
                }
                assignedDate
                notes
            }
            updatedAt
        }
    }
`;
// ─── Suspensive Conditions & Conclusion Gate ──────────────────────────────

export const ADD_SUSPENSIVE_CONDITION = gql`
    mutation AddSuspensiveCondition($dealId: ID!, $inputData: AddSuspensiveConditionInput!) {
        addSuspensiveCondition(dealId: $dealId, inputData: $inputData) {
            id
            suspensiveConditions {
                id
                description
                dueDate
                completed
                completedDate
                overdue
            }
            outstandingConditions {
                kind
                label
                dueDate
                overdue
            }
            needsAttention
        }
    }
`;

export const UPDATE_SUSPENSIVE_CONDITION = gql`
    mutation UpdateSuspensiveCondition($dealId: ID!, $conditionId: ID!, $inputData: UpdateSuspensiveConditionInput!) {
        updateSuspensiveCondition(dealId: $dealId, conditionId: $conditionId, inputData: $inputData) {
            id
            suspensiveConditions {
                id
                description
                dueDate
                completed
                completedDate
                overdue
            }
            outstandingConditions {
                kind
                label
                dueDate
                overdue
            }
            needsAttention
        }
    }
`;

export const REMOVE_SUSPENSIVE_CONDITION = gql`
    mutation RemoveSuspensiveCondition($dealId: ID!, $conditionId: ID!) {
        removeSuspensiveCondition(dealId: $dealId, conditionId: $conditionId) {
            id
            suspensiveConditions {
                id
                description
                dueDate
                completed
                completedDate
                overdue
            }
            outstandingConditions {
                kind
                label
                dueDate
                overdue
            }
            needsAttention
        }
    }
`;

export const SET_DEPOSIT_RECEIVED = gql`
    mutation SetDepositReceived($dealId: ID!, $received: Boolean!, $receivedDate: Date) {
        setDepositReceived(dealId: $dealId, received: $received, receivedDate: $receivedDate) {
            id
            depositReceived
            depositReceivedDate
            outstandingConditions {
                kind
                label
                dueDate
                overdue
            }
            needsAttention
        }
    }
`;

export const SET_BOND_GRANTED = gql`
    mutation SetBondGranted($dealId: ID!, $granted: Boolean!, $grantedDate: Date) {
        setBondGranted(dealId: $dealId, granted: $granted, grantedDate: $grantedDate) {
            id
            bondGranted
            bondGrantedDate
            outstandingConditions {
                kind
                label
                dueDate
                overdue
            }
            needsAttention
        }
    }
`;

export const SET_SENT_TO_SMART_TRACK = gql`
    mutation SetSentToSmartTrack($dealId: ID!, $sent: Boolean!, $sentDate: Date) {
        setSentToSmartTrack(dealId: $dealId, sent: $sent, sentDate: $sentDate) {
            id
            sentToSmartTrack
            sentToSmartTrackDate
            outstandingConditions {
                kind
                label
                dueDate
                overdue
            }
            needsAttention
        }
    }
`;

export const MARK_DEAL_AS_CONCLUDED = gql`
    mutation MarkDealAsConcluded($id: ID!) {
        markDealAsConcluded(id: $id) {
            id
            status
            dateConcluded
            dateConditionsMet
        }
    }
`;

// ─── Transfer Progress (post-CONCLUDED conveyancing milestones) ──────────

const TRANSFER_PROGRESS_FRAGMENT = `
    transferProgress {
        hoaApplicable
        sarsClearance { date notes }
        municipalityClearance { date notes }
        hoaClearance { date notes }
        sellerSignedTransferAttorney { date notes }
        purchaserSignedBondAttorney { date notes }
        purchaserSignedTransferAttorney { date notes }
        lodgement { date notes }
        registrationPreparation { date notes }
        registered { date notes }
    }
`;

export const SET_TRANSFER_MILESTONE = gql`
    mutation SetTransferMilestone($dealId: ID!, $key: TransferMilestoneKey!, $inputData: TransferMilestoneInput!) {
        setTransferMilestone(dealId: $dealId, key: $key, inputData: $inputData) {
            id
            registrationDate
            ${TRANSFER_PROGRESS_FRAGMENT}
        }
    }
`;

export const SET_HOA_APPLICABLE = gql`
    mutation SetHoaApplicable($dealId: ID!, $applicable: Boolean!) {
        setHoaApplicable(dealId: $dealId, applicable: $applicable) {
            id
            ${TRANSFER_PROGRESS_FRAGMENT}
        }
    }
`;

export const SET_FICA_CHECKED = gql`
    mutation SetFicaChecked($dealId: ID!, $checked: Boolean!) {
        setFicaChecked(dealId: $dealId, checked: $checked) {
            id
            ficaChecked
            ficaCheckDate
        }
    }
`;

// ─── Compliance Certificates (electrical / gas / beetle, post-CONCLUDED) ──

export const SET_COMPLIANCE_CERTIFICATE = gql`
    mutation SetComplianceCertificate($dealId: ID!, $kind: ComplianceCertificateKind!, $inputData: ComplianceCertificateInput!) {
        setComplianceCertificate(dealId: $dealId, kind: $kind, inputData: $inputData) {
            id
            complianceCertificates {
                electrical {
                    companyId { id companyName tradingName }
                    dateRequested
                    dateReceived
                    sentToAttorney
                }
                gas {
                    companyId { id companyName tradingName }
                    dateRequested
                    dateReceived
                    sentToAttorney
                }
                beetle {
                    companyId { id companyName tradingName }
                    dateRequested
                    dateReceived
                    sentToAttorney
                }
            }
        }
    }
`;

// ─── Revert Flow (CONCLUDED -> PENDING) ───────────────────────────────────

export const REQUEST_DEAL_REVERT = gql`
    mutation RequestDealRevert($dealId: ID!, $reason: String!) {
        requestDealRevert(dealId: $dealId, reason: $reason) {
            id
            status
            revertRequests {
                id
                requestedBy { id firstName lastName }
                requestedAt
                reason
                status
                decidedBy { id firstName lastName }
                decidedAt
                decisionNote
            }
        }
    }
`;

export const APPROVE_DEAL_REVERT = gql`
    mutation ApproveDealRevert($dealId: ID!, $requestId: ID!, $decisionNote: String) {
        approveDealRevert(dealId: $dealId, requestId: $requestId, decisionNote: $decisionNote) {
            id
            status
            dateConcluded
            dateConditionsMet
            revertRequests {
                id
                requestedBy { id firstName lastName }
                requestedAt
                reason
                status
                decidedBy { id firstName lastName }
                decidedAt
                decisionNote
            }
        }
    }
`;

export const REJECT_DEAL_REVERT = gql`
    mutation RejectDealRevert($dealId: ID!, $requestId: ID!, $decisionNote: String) {
        rejectDealRevert(dealId: $dealId, requestId: $requestId, decisionNote: $decisionNote) {
            id
            status
            revertRequests {
                id
                requestedBy { id firstName lastName }
                requestedAt
                reason
                status
                decidedBy { id firstName lastName }
                decidedAt
                decisionNote
            }
        }
    }
`;

export const REVERT_DEAL_DIRECTLY = gql`
    mutation RevertDealDirectly($dealId: ID!, $reason: String!) {
        revertDealDirectly(dealId: $dealId, reason: $reason) {
            id
            status
            dateConcluded
            dateConditionsMet
            revertRequests {
                id
                requestedBy { id firstName lastName }
                requestedAt
                reason
                status
                decidedBy { id firstName lastName }
                decidedAt
                decisionNote
            }
        }
    }
`;
